/**
 * API e stato server della watchlist (SPEC-004).
 *
 * ⚠️ Prezzi/importi sono STRINGHE decimali (ADR-0006): mai `parseFloat` per
 * calcoli, solo `lib/format.ts` per la visualizzazione.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type WatchlistItem = {
  id: string;
  instrument: {
    id: string;
    name: string;
    isin: string | null;
    symbol: string | null;
    instrumentType: string;
    currency: string;
    mappingStatus: string | null;
    /** Mercato di quotazione risolto automaticamente (SPEC-010), `null` se non ancora risolto. */
    marketMic: string | null;
    /** `resolved`/`manual`/`ambiguous`/`unresolved` (SPEC-010 §4.3). */
    marketResolutionStatus: string;
  };
  addedAt: string;
  addedPrice: string | null;
  addedPriceCurrency: string | null;
  lastPrice: string | null;
  lastPriceCurrency: string | null;
  lastPriceAt: string | null;
  /** Valuta base dell'utente: sempre presente, anche quando non serve conversione. */
  baseCurrency: string;
  /** `addedPrice` convertito in `baseCurrency`; `null` se già nella stessa valuta o cambio non disponibile. */
  addedPriceBase: string | null;
  /** `lastPrice` convertito in `baseCurrency`; stessa regola di `addedPriceBase`. */
  lastPriceBase: string | null;
  deltaPct: string | null;
  targetPrice: string | null;
  notes: string | null;
  inPortfolio: boolean;
  /** Ultima analisi AI completata per lo strumento (SPEC-007), `null` se non ce n'è ancora una. */
  latestAnalysis: { id: string; rating: string | null; completedAt: string } | null;
  /** Chi ha messo la voce (F-181): l'utente o un automatismo. */
  source: WatchlistSource;
  /** Quando la voce uscirà da sola se nessun automatismo la ripropone; `null` = non esce. */
  autoRemovalAt: string | null;
};

export type WatchlistSource = 'manual' | 'daily_pick' | 'rotation' | 'revival' | 'news_review';

/** Da dove arriva una voce automatica, detto a chi legge la pagina. */
export const WATCHLIST_SOURCE_LABELS: Record<Exclude<WatchlistSource, 'manual'>, string> = {
  daily_pick: 'dal titolo del giorno',
  rotation: 'dalla rotazione',
  revival: 'dal ripescaggio dei candidati scaduti',
  news_review: 'dalla lettura delle notizie',
};

export type WatchlistStats = {
  itemsCount: number;
  distinctInstrumentsGlobal: number;
  /** Voci e limite per ambito (F-195): borsa e crypto hanno posti separati. */
  limits?: Record<'stock' | 'crypto', { count: number; max: number }>;
};

export type WatchlistPricePoint = {
  capturedAt: string;
  price: string;
  currency: string;
  source: string;
};

export const watchlistKeys = {
  list: ['watchlist', 'list'] as const,
  stats: ['watchlist', 'stats'] as const,
  history: (itemId: string) => ['watchlist', 'history', itemId] as const,
};

export function useWatchlist() {
  return useQuery({
    queryKey: watchlistKeys.list,
    queryFn: ({ signal }) => api.get<{ items: WatchlistItem[]; total: number }>('/watchlist', signal),
  });
}

export function useWatchlistStats() {
  return useQuery({
    queryKey: watchlistKeys.stats,
    queryFn: ({ signal }) => api.get<WatchlistStats>('/watchlist/stats', signal),
  });
}

export function useWatchlistItemHistory(itemId: string, enabled: boolean) {
  return useQuery({
    queryKey: watchlistKeys.history(itemId),
    queryFn: ({ signal }) =>
      api.get<{ instrumentId: string; items: WatchlistPricePoint[] }>(
        `/watchlist/items/${itemId}/history`,
        signal,
      ),
    enabled,
  });
}

export function useAddWatchlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      instrumentId?: string;
      isin?: string;
      symbol?: string;
      notes?: string;
    }) => api.post<WatchlistItem>('/watchlist/items', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: watchlistKeys.list });
      void queryClient.invalidateQueries({ queryKey: watchlistKeys.stats });
      // La pagina di dettaglio mostra la stessa appartenenza da un'altra
      // query aggregata: senza questa riga il titolo appena aggiunto continua
      // a dichiararsi "non in watchlist" finché non si ricarica.
      void queryClient.invalidateQueries({ queryKey: ['instruments', 'detail'] });
      // F-085: la ricerca manuale calcola `visible` dalle liste dell'utente.
      // Entrare in watchlist è precisamente ciò che rende apribile la scheda:
      // senza rilettura la riga continuerebbe a offrire di seguire un titolo
      // che ora si segue già.
      void queryClient.invalidateQueries({ queryKey: ['instruments', 'search'] });
    },
  });
}

export function useRemoveWatchlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.delete<void>(`/watchlist/items/${itemId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: watchlistKeys.list });
      void queryClient.invalidateQueries({ queryKey: watchlistKeys.stats });
    },
  });
}

/** «Tieni» (F-181): la voce automatica diventa dell'utente e non esce più da sola. */
export function useKeepWatchlistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.post<void>(`/watchlist/items/${itemId}/keep`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: watchlistKeys.list });
    },
  });
}
